import React from 'react';
import { Link } from 'react-router-dom';
import type { CardRef } from '../client';
import {
  expandMentionsToMarkdown,
  findMentions,
  MentionableAgent,
  MentionMatch,
} from './mentions';
import {
  cardUrl,
  expandCardRefsToMarkdown,
  findCardRefSpans,
  refTitleExcerpt,
} from './card-refs';
import {
  expandWikiRefsToMarkdown,
  WikiRefProject,
  WIKI_REF_PATTERN,
  wikiUrl,
} from './wiki-refs';
import { expandWikilinks } from '../wiki/wiki-markdown';

/**
 * One shared expansion pipeline for every surface that renders stored
 * text (chat, cards, wiki, work streams, notifications). Turns the four
 * reference idioms — @Agent Name, #KEY-N, wiki:KEY/path, [[wikilink]] —
 * into markdown links, uniformly skipping code spans, code fences, and
 * text already inside markdown link syntax. Runs BEFORE markdown
 * rendering so DOMPurify sanitization applies to everything it emits.
 */

export interface RichRefContext {
  teamId: string | number;
  agents?: MentionableAgent[];
  cardRefs?: CardRef[];
  /**
   * The team's real projects ({key, projectId}) — resolves wiki:KEY/path
   * and [[KEY:path]] regardless of whether any cards exist.
   */
  projects?: WikiRefProject[];
  /**
   * Present when rendering happens "inside" a wiki (a page, or a surface
   * scoped to one project): enables bare [[path]] resolution, ./ and ../
   * relative paths, and red links for missing pages.
   */
  wiki?: { projectId?: number; pagePath: string; knownPaths?: Set<string> };
}

const CODE_REGION_PATTERN = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*`/g;

/**
 * Apply a text transform to the stretches of `text` that are NOT inside
 * an inline code span or fenced code block, leaving code verbatim. An
 * unterminated fence swallows the rest of the text (matching how
 * markdown renders it).
 */
export function mapNonCodeSegments(
  text: string,
  transform: (segment: string) => string,
): string {
  if (!text) return text;
  let out = '';
  let last = 0;
  for (const match of text.matchAll(CODE_REGION_PATTERN)) {
    const start = match.index!;
    if (start > last) out += transform(text.slice(last, start));
    out += match[0];
    last = start + match[0].length;
  }
  if (last < text.length) out += transform(text.slice(last));
  return out;
}

export { insideMarkdownLink } from './ref-guards';

/**
 * The project bare [[path]] wikilinks resolve against: an explicit wiki
 * context wins; otherwise a team with exactly one project is
 * unambiguous; otherwise none (cross-project [[KEY:path]] still works).
 */
function wikilinkProjectId(ctx: RichRefContext): number | undefined {
  if (ctx.wiki?.projectId) return ctx.wiki.projectId;
  if ((ctx.projects || []).length === 1) return ctx.projects![0].projectId;
  return undefined;
}

export function expandAllRefs(text: string, ctx: RichRefContext): string {
  if (!text) return text;
  return mapNonCodeSegments(text, (segment) => {
    let out = expandMentionsToMarkdown(segment, ctx.agents, ctx.teamId);
    out = expandCardRefsToMarkdown(out, ctx.cardRefs, ctx.teamId);
    out = expandWikiRefsToMarkdown(out, ctx.projects, ctx.teamId);
    out = expandWikilinks(out, {
      teamId: ctx.teamId,
      projectId: wikilinkProjectId(ctx),
      pagePath: ctx.wiki?.pagePath ?? '',
      projects: ctx.projects,
      knownPaths: ctx.wiki?.knownPaths,
    });
    return out;
  });
}

interface PlainSpan {
  start: number;
  end: number;
  node: (key: string) => React.ReactNode;
}

/**
 * Plain-text counterpart of expandAllRefs for surfaces that render text
 * as React nodes rather than markdown (user chat bubbles): @mentions,
 * #KEY-N (with adjacent duplicate titles consumed), and wiki:KEY/path
 * become in-app links. Mentions render through `renderMention` so the
 * caller decides between agent links and member modals.
 */
export function renderPlainTextWithRefs(
  text: string,
  ctx: RichRefContext,
  renderMention: (match: MentionMatch, key: string) => React.ReactNode,
): React.ReactNode {
  if (!text) return text;
  const spans: PlainSpan[] = [];
  for (const match of findMentions(text, ctx.agents)) {
    spans.push({
      start: match.start,
      end: match.end,
      node: (key) => renderMention(match, key),
    });
  }
  for (const span of findCardRefSpans(text, ctx.cardRefs, false)) {
    spans.push({
      start: span.start,
      end: span.end,
      node: (key) => (
        <Link key={key} to={cardUrl(ctx.teamId, span.ref)}>
          {span.ref.reference} ({refTitleExcerpt(span.ref.title)})
        </Link>
      ),
    });
  }
  const byKey = new Map(
    (ctx.projects || []).map((project) => [
      project.key.toUpperCase(),
      project.projectId,
    ]),
  );
  if (byKey.size > 0) {
    const pattern = new RegExp(WIKI_REF_PATTERN.source, 'g');
    for (const match of text.matchAll(pattern)) {
      const projectId = byKey.get(match[1].toUpperCase());
      if (!projectId) continue;
      const path = match[2].replace(/[.,;:!?)\]]+$/, '');
      if (!path) continue;
      const token = `wiki:${match[1]}/${path}`;
      spans.push({
        start: match.index!,
        end: match.index! + token.length,
        node: (key) => (
          <Link key={key} to={wikiUrl(ctx.teamId, projectId, path)}>
            {token}
          </Link>
        ),
      });
    }
  }
  spans.sort((a, b) => a.start - b.start);
  const nodes: React.ReactNode[] = [];
  let last = 0;
  spans.forEach((span, index) => {
    if (span.start < last) return;
    if (span.start > last) nodes.push(text.slice(last, span.start));
    nodes.push(span.node(`ref-${index}`));
    last = span.end;
  });
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length > 0 ? nodes : text;
}
